import { useCallback, useState, type FC } from "react";
import { Lock } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import AudienceListSelect from "src/components/reusable/AudienceListSelect/AudienceListSelect";
import type { LocationState } from "../EngagementInterfaces";
import AiGeneratorCard from "./CampaignSetup/AiGeneratorCard";
import ChannelSelector from "./CampaignSetup/ChannelSelector";
import { useCampaignSetup } from "./CampaignSetup/UseCampaignSetup";
import CampaignPageLayout from "./shared/CampaignPageLayout";
import CampaignStepCard from "./shared/CampaignStepCard";
import CampaignFormField from "./shared/CampaignFormField";
import { usePeopleReachedText } from "./shared/UsePeopleReachedText";
import CampaignTextField from "./shared/CampaignTextField";
import CampaignFlowActions from "./shared/CampaignFlowActions";
import CampaignNextButton from "./shared/CampaignNextButton";
import {
  ShowFieldErrorsContext,
  getFieldErrorId,
  useRequiredFieldError,
} from "./shared/UseRequiredFieldError";
import "./Styles/CreateCampaign.scss";
import "./CampaignSetup/CampaignSetup.scss";

const FIELD_IDS = {
  channelLabel: "campaign-setup-channel-label",
  campaignName: "campaign-setup-name",
  company: "campaign-setup-company",
  sendTo: "campaign-setup-send-to",
  dontSendTo: "campaign-setup-dont-send-to",
};

const POPUP_CLASS = "campaign-flow__popup";

const CreateCampaign: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locationState = useLocation().state as LocationState | null;

  const {
    channel,
    campaignName,
    companyName,
    sendTo,
    excludeList,
    audienceOptions,
    excludeOptions,
    isListsLoading,
    canSubmit,
    isAIGeneratorOn,
    isAIGeneratorDisabled,
    setChannel,
    setCampaignName,
    setSendTo,
    setExcludeList,
    handleAIGeneratorChange,
    handleSubmit,
  } = useCampaignSetup(locationState?.campaign_id, {
    channel: locationState?.channel,
    useAi: locationState?.use_ai,
    listName: locationState?.list_name,
  });

  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const handleBack = useCallback(() => navigate(-1), [navigate]);
  const revealFieldErrors = useCallback(() => setShowFieldErrors(true), []);
  const sendToField = useRequiredFieldError(sendTo, true, showFieldErrors);
  const peopleReachedText = usePeopleReachedText(audienceOptions, sendTo);

  return (
    <CampaignPageLayout>
      <ShowFieldErrorsContext.Provider value={showFieldErrors}>
        <CampaignStepCard
          title={t("campaignFlow.createCampaignNew.title")}
          subtitle={t("campaignFlow.createCampaignNew.cardSubtitle")}
        >
          <CampaignFormField
            label={t("campaignFlow.createCampaignNew.channel")}
            labelId={FIELD_IDS.channelLabel}
            required
            hasError={showFieldErrors && !channel}
          >
            <ChannelSelector
              value={channel}
              onChange={setChannel}
              labelledBy={FIELD_IDS.channelLabel}
            />
          </CampaignFormField>

          <CampaignTextField
            id={FIELD_IDS.campaignName}
            label={t("campaignFlow.common.campaignName")}
            value={campaignName}
            onChange={setCampaignName}
            placeholder={t("campaignFlow.createCampaignNew.campaignNamePlaceholder")}
            required
          />

          <CampaignFormField
            label={t("campaignFlow.createCampaignNew.company")}
            htmlFor={FIELD_IDS.company}
            required
          >
            <StyledTextInput
              id={FIELD_IDS.company}
              value={companyName}
              disabled
              suffix={<Lock className="campaign-setup__lock-icon" aria-hidden />}
              className="campaign-flow__input"
            />
          </CampaignFormField>

          <div className="campaign-flow__field-row">
            <CampaignFormField
              label={t("campaignFlow.createCampaignNew.sendTo")}
              htmlFor={FIELD_IDS.sendTo}
              required
              hasError={sendToField.hasError}
              helperText={peopleReachedText}
            >
              <AudienceListSelect
                id={FIELD_IDS.sendTo}
                value={sendTo}
                options={audienceOptions}
                onChange={setSendTo}
                onBlur={sendToField.handleBlur}
                aria-invalid={sendToField.hasError}
                aria-describedby={
                  sendToField.hasError ? getFieldErrorId(FIELD_IDS.sendTo) : undefined
                }
                loading={isListsLoading}
                placeholder={t("campaignFlow.createCampaignNew.selectAudience")}
                popupClassName={POPUP_CLASS}
              />
            </CampaignFormField>

            <CampaignFormField
              label={t("campaignFlow.createCampaignNew.dontSendTo")}
              htmlFor={FIELD_IDS.dontSendTo}
              hint={t("campaignFlow.createCampaignNew.optionalHint")}
              helperText={t("campaignFlow.aiWizard.basics.exclusionHint")}
            >
              <AudienceListSelect
                id={FIELD_IDS.dontSendTo}
                value={excludeList}
                options={excludeOptions}
                onChange={setExcludeList}
                loading={isListsLoading}
                placeholder={t("campaignFlow.createCampaignNew.selectExclusions")}
                popupClassName={POPUP_CLASS}
              />
            </CampaignFormField>
          </div>

          {channel === "email" && (
            <AiGeneratorCard
              checked={isAIGeneratorOn}
              disabled={isAIGeneratorDisabled}
              onChange={handleAIGeneratorChange}
            />
          )}
        </CampaignStepCard>
      </ShowFieldErrorsContext.Provider>

      <CampaignFlowActions onBack={handleBack}>
        <CampaignNextButton
          canProceed={canSubmit}
          onNext={handleSubmit}
          onBlockedClick={revealFieldErrors}
        >
          {t("campaignFlow.createCampaignNew.next")}
        </CampaignNextButton>
      </CampaignFlowActions>
    </CampaignPageLayout>
  );
};

export default CreateCampaign;
